import { useState } from 'react';
import { ShoppingCart, X, Menu } from 'lucide-react';
import { Link, useLocation } from "react-router-dom";
import { useCart } from './CartContext';
import afthlogo from '../assets/images/afth-logo.jpg';

export default function Navbar() {
  const location = useLocation();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Use CartContext instead of local state
  const { cartItems, removeFromCart, getCartTotal, getCartCount } = useCart();
  const totalPrice = getCartTotal();

  // Helper function to check if link is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Close mobile menu when a link is clicked
  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="border-b bg-white sticky top-0 z-50 shadow-sm">
      <div className="w-full px-4 sm:px-6 md:px-8 py-3 sm:py-4 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <Link to="/" onClick={handleLinkClick}>
            <img 
              src={afthlogo} 
              alt="CAC AFTH Logo" 
              className="h-12 sm:h-14 md:h-16 w-auto object-contain"
            />
          </Link>
        </div>
        
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          <Link 
            to="/" 
            className={`font-medium no-underline transition-colors ${
              isActive('/') 
                ? 'text-blue-500 border-b-2 border-blue-500' 
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            Home
          </Link>
          
          <Link 
            to="/about" 
            className={`no-underline transition-colors ${
              isActive('/about') 
                ? 'text-blue-500 border-b-2 border-blue-500 font-medium' 
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            About Us
          </Link>
          
          <Link 
            to="/beneficiaries" 
            className={`no-underline transition-colors ${
              isActive('/beneficiaries') 
                ? 'text-blue-500 border-b-2 border-blue-500 font-medium' 
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            Our Beneficiaries
          </Link>
          
          <Link
            to="/shop" 
            className={`no-underline transition-colors ${
              isActive('/shop') 
                ? 'text-blue-500 border-b-2 border-blue-500 font-medium' 
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            Shop
          </Link>

          <Link 
            to="/events" 
            className={`no-underline transition-colors ${
              isActive('/events') 
                ? 'text-blue-500 border-b-2 border-blue-500 font-medium' 
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            Events
          </Link>
        </nav>

        {/* Right side - Cart and Mobile Menu */}
        <div className="flex items-center gap-4">
          {/* Cart Icon */}
          <div 
            className="relative"
            onMouseEnter={() => !isMobileMenuOpen && setIsCartOpen(true)}
            onMouseLeave={() => setIsCartOpen(false)}
          >
            <div 
              className="relative cursor-pointer"
              onClick={() => setIsCartOpen(!isCartOpen)}
            >
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 hover:text-gray-900 transition-colors" />
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-semibold">
                  {getCartCount()}
                </span>
              )}
            </div>

            {/* Cart Dropdown */}
            {isCartOpen && (
              <div className="fixed sm:absolute right-4 sm:right-0 left-4 sm:left-auto mt-0 pt-2 sm:w-96 z-50">
                <div className="bg-white rounded-lg shadow-xl border border-gray-200">
                  {/* Header */}
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-semibold text-base sm:text-lg text-gray-900">Shopping Cart</h3>
                    <button 
                      onClick={() => setIsCartOpen(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Cart Items */}
                  <div className="max-h-[50vh] sm:max-h-96 overflow-y-auto">
                    {cartItems.length === 0 ? (
                      <div className="px-4 py-8 text-center text-gray-500">
                        Your cart is empty
                      </div>
                    ) : (
                      <div className="px-4 py-3">
                        {cartItems.map((item, index) => (
                          <div key={`${item.id}-${item.size}-${index}`} className="flex gap-3 py-3 border-b border-gray-100 last:border-0">
                            <img 
                              src={item.image} 
                              alt={item.name}
                              className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 text-sm truncate">{item.name}</h4>
                              {item.size && <p className="text-gray-500 text-xs">Size: {item.size}</p>}
                              <p className="text-gray-600 text-sm mt-1">
                                ${item.price.toFixed(2)} × {item.quantity}
                              </p>
                            </div>
                            <div className="flex flex-col items-end justify-between">
                              <button 
                                onClick={() => removeFromCart(item.id, item.size)}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <span className="font-semibold text-gray-900 text-sm">
                                ${(item.price * item.quantity).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  {cartItems.length > 0 && (
                    <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-semibold text-gray-900">Total:</span>
                        <span className="font-bold text-lg sm:text-xl text-gray-900">${totalPrice.toFixed(2)}</span>
                      </div>
                      <Link 
                        to="/cart"
                        className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2.5 sm:py-3 rounded-lg transition-colors mb-2 block text-center no-underline text-sm sm:text-base"
                        onClick={() => setIsCartOpen(false)}
                      >
                        View Cart
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-gray-700 hover:text-gray-900 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white">
          <nav className="px-4 py-3 space-y-1">
            <Link 
              to="/" 
              onClick={handleLinkClick}
              className={`block py-3 px-3 rounded-lg no-underline transition-colors ${
                isActive('/') 
                  ? 'bg-blue-50 text-blue-600 font-medium' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Home
            </Link>
            
            <Link 
              to="/about" 
              onClick={handleLinkClick}
              className={`block py-3 px-3 rounded-lg no-underline transition-colors ${
                isActive('/about') 
                  ? 'bg-blue-50 text-blue-600 font-medium' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              About Us
            </Link>
            
            <Link 
              to="/beneficiaries" 
              onClick={handleLinkClick}
              className={`block py-3 px-3 rounded-lg no-underline transition-colors ${
                isActive('/beneficiaries') 
                  ? 'bg-blue-50 text-blue-600 font-medium' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Our Beneficiaries
            </Link>
            
            <Link 
              to="/shop" 
              onClick={handleLinkClick}
              className={`block py-3 px-3 rounded-lg no-underline transition-colors ${
                isActive('/shop') 
                  ? 'bg-blue-50 text-blue-600 font-medium' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Shop
            </Link>
            
            <Link 
              to="/events" 
              onClick={handleLinkClick}
              className={`block py-3 px-3 rounded-lg no-underline transition-colors ${
                isActive('/events') 
                  ? 'bg-blue-50 text-blue-600 font-medium' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Events
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}